import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { getSiteInspectionPingUrl } from '../utils/api';
import { Camera, FileText, MapPin, Printer, RefreshCw, User } from 'lucide-react';
import { InfoButton } from './InfoModal';
import PageHeading from './PageHeading';

function parsePhotoUrls(row) {
  if (!row) return [];
  if (Array.isArray(row)) return row;
  if (typeof row === 'string') {
    try {
      const p = JSON.parse(row);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

function photoSrc(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/** Consolidated Info text (same field layout as worker WebView `siFormatPointInfo`). English-only UI; i18n later. */
function formatSiteInspectionPointInfo(d) {
  if (!d) return 'No consolidated info registered for this row.';
  const lines = [];
  lines.push('[Item summary]');
  if (d.category) lines.push(`• Category: ${String(d.category)}`);
  if (d.title) lines.push(`• Inspection item: ${String(d.title)}`);
  if (d.method_tool) lines.push(`• Method / tool: ${String(d.method_tool)}`);
  if (d.standard_ref) lines.push(`• Design / standard: ${String(d.standard_ref)}`);
  const instr = d.instruction != null ? String(d.instruction).trim() : '';
  const stdRef = d.standard_ref != null ? String(d.standard_ref).trim() : '';
  if (instr && instr !== stdRef) lines.push(`• Notes: ${instr}`);
  if (d.value_unit) lines.push(`• Unit: ${String(d.value_unit)}`);
  const wtag = d.worker_requirement_tag != null ? String(d.worker_requirement_tag).trim() : '';
  const ttag = d.inspection_term_tag != null ? String(d.inspection_term_tag).trim() : '';
  const ltag = d.inspection_location_tag != null ? String(d.inspection_location_tag).trim() : '';
  if (wtag || ttag || ltag) {
    lines.push('');
    lines.push('[Context tags (when set)]');
    if (wtag) lines.push(`• Worker requirement: ${wtag}`);
    if (ttag) lines.push(`• Inspection term: ${ttag}`);
    if (ltag) lines.push(`• Location / zone: ${ltag}`);
  }
  lines.push('');
  lines.push('Use + Photo to attach field photos for this line.');
  const hasBullets = lines.some((x) => x.startsWith('•'));
  if (!hasBullets) {
    return 'No consolidated info yet. See Method / Standard and Notes columns.\n\nUse + Photo for field evidence.';
  }
  return lines.join('\n');
}

const SITUATION_FALLBACK = ['FIRST_VISIT', 'MONTHLY', 'SIX_MONTHLY', 'ANNUAL', 'CHANGE', 'GENERAL'];

/** Let axios resolve on 404/503 so the browser does not log red failed XHR lines (empty checklist). */
function siAxiosValidateEmptyOk(status) {
  return (status >= 200 && status < 300) || status === 404 || status === 503;
}

function siDataListFromResponse(res) {
  if (!res || res.status === 404 || res.status === 503) return [];
  return res.data?.data ?? [];
}

/**
 * Shared checklist UI ?? full page or inline on Amend Compliance / Process stages.
 * @param {'page'|'inline'} variant
 */
export default function SiteInspectionChecklistContent({
  variant = 'page',
  defaultBuildingId = '',
  defaultJobId = '',
  defaultSsCode = '',
  defaultSituationKey = 'FIRST_VISIT',
  /** When set, building is resolved from application (not shown in URL). */
  defaultApplicationId = '',
  /** Shown in embedded header when set (portfolio / form name). */
  buildingDisplayName = '',
  /** Denser single card: toolbar + context + table (embedded workflow) */
  compactInline = false,
}) {
  const isInline = variant === 'inline';
  const mergeCard = isInline && compactInline;
  const [buildingId, setBuildingId] = useState(defaultBuildingId || '');
  const [jobId, setJobId] = useState(defaultJobId || '');
  const [ssCode, setSsCode] = useState(defaultSsCode || '');
  const [situationKey, setSituationKey] = useState(defaultSituationKey || 'FIRST_VISIT');
  /** Optional: filter merged/definitions rows by DB tags (NULL tag on row = applies to any) */
  const [workerRequirementTag, setWorkerRequirementTag] = useState('');
  const [inspectionTermTag, setInspectionTermTag] = useState('');
  const [inspectionLocationTag, setInspectionLocationTag] = useState('');
  const [manualRefreshKey, setManualRefreshKey] = useState(0);

  useEffect(() => {
    setBuildingId(defaultBuildingId || '');
    setJobId(defaultJobId || '');
    setSsCode(defaultSsCode || '');
    setSituationKey(defaultSituationKey || 'FIRST_VISIT');
  }, [defaultBuildingId, defaultJobId, defaultSsCode, defaultSituationKey]);

  const { data: amendApplication } = useQuery({
    queryKey: ['compliance-amendment', defaultApplicationId],
    queryFn: async () => {
      const res = await api.get(`/compliance-amendments/${defaultApplicationId}`);
      return res.data;
    },
    enabled: !!defaultApplicationId,
    retry: false,
  });

  useEffect(() => {
    const bid = amendApplication?.building_id;
    if (bid == null || bid === '') return;
    setBuildingId(String(bid));
  }, [amendApplication?.building_id]);

  const needBuildingList = variant === 'page' || mergeCard;
  const { data: buildingsPickList } = useQuery({
    queryKey: ['buildings', 'site-inspection-picker'],
    queryFn: async ({ signal }) => {
      const res = await api.get('/buildings', { params: { page: 1, limit: 2000, search: '' }, signal });
      return res.data?.data ?? [];
    },
    enabled: needBuildingList,
    staleTime: 60_000,
    retry: false,
  });
  const buildingsArray = Array.isArray(buildingsPickList) ? buildingsPickList : [];

  const resolvedBuildingLabel = useMemo(() => {
    if ((buildingDisplayName || '').trim()) return buildingDisplayName.trim();
    if (defaultApplicationId && amendApplication) {
      const n = (amendApplication.form11a_building_name || '').trim();
      const a = (amendApplication.form11a_address || '').trim();
      if (n && a) return `${n} — ${a}`;
      if (n) return n;
      if (a) return a;
    }
    if (!buildingId) return '';
    const b = buildingsArray.find((x) => String(x.id) === String(buildingId));
    if (b) {
      const n = (b.name || '').trim();
      const a = (b.address || '').trim();
      if (n && a) return `${n} — ${a}`;
      return n || a || '';
    }
    return '';
  }, [buildingDisplayName, buildingId, buildingsArray, defaultApplicationId, amendApplication]);

  /** Ping: banner + enable polling only when the API responds with { ok: true }. */
  const { data: pingState, isFetched: pingFetched } = useQuery({
    queryKey: ['site-inspection-points', 'ping'],
    queryFn: async () => {
      try {
        const res = await fetch(getSiteInspectionPingUrl(), {
          credentials: 'include',
        });
        const j = await res.json().catch(() => ({}));
        const ok = res.ok && j?.ok === true;
        return { ok, status: res.status, networkError: false };
      } catch {
        return { ok: false, status: 0, networkError: true };
      }
    },
    staleTime: 30_000,
    retry: false,
  });

  const siApiLive = pingFetched && pingState?.ok === true;

  const { data: ssCodesData } = useQuery({
    queryKey: ['workers', 'ss-codes', 'site-inspection-checklist'],
    queryFn: async ({ signal }) => {
      const res = await api.get('/workers/ss-codes', { signal });
      return res.data?.data ?? res.data ?? [];
    },
    retry: false,
  });
  const ssCodeRows = useMemo(() => {
    const raw = Array.isArray(ssCodesData) ? ssCodesData : [];
    return raw
      .map((item) => {
        if (typeof item === 'string') {
          const code = item.trim();
          return code ? { code, label: code } : null;
        }
        if (item && typeof item === 'object' && item.code) {
          const code = String(item.code).trim();
          if (!code) return null;
          const name = item.name ? String(item.name).trim() : '';
          return { code, label: name ? `${code} — ${name}` : code };
        }
        return null;
      })
      .filter(Boolean);
  }, [ssCodesData]);

  const { data: situationsData } = useQuery({
    queryKey: ['site-inspection-points', 'situations'],
    queryFn: async () => {
      const res = await api.get('/site-inspection-points/situations', {
        validateStatus: siAxiosValidateEmptyOk,
      });
      return siDataListFromResponse(res);
    },
    retry: false,
  });
  const situationOptions = useMemo(() => {
    const d = situationsData;
    const fallback = SITUATION_FALLBACK;
    if (!Array.isArray(d) || d.length === 0) return fallback;
    const mapped = d
      .map((x) => (typeof x === 'string' ? x : x && typeof x === 'object' && x.key ? String(x.key) : String(x)))
      .filter(Boolean);
    return mapped.length ? mapped : fallback;
  }, [situationsData]);

  const mergedParamsOk = !!ssCode && !!situationKey && !!buildingId && !!jobId;

  const {
    data: definitions,
    isLoading: isLoadingDefs,
    refetch: refetchDefs,
  } = useQuery({
    queryKey: [
      'site-inspection-points',
      'definitions',
      ssCode,
      situationKey,
      buildingId,
      jobId,
      workerRequirementTag,
      inspectionTermTag,
      inspectionLocationTag,
      manualRefreshKey,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('ssCode', ssCode);
      params.set('situationKey', situationKey);
      const wt = String(workerRequirementTag || '').trim();
      const tt = String(inspectionTermTag || '').trim();
      const lt = String(inspectionLocationTag || '').trim();
      if (wt) params.set('workerTag', wt);
      if (tt) params.set('inspectionTermTag', tt);
      if (lt) params.set('inspectionLocationTag', lt);
      const reqOpts = { validateStatus: siAxiosValidateEmptyOk };
      if (mergedParamsOk) {
        params.set('buildingId', buildingId);
        params.set('jobId', jobId);
        const res = await api.get(`/site-inspection-points/definitions/merged?${params.toString()}`, reqOpts);
        return siDataListFromResponse(res);
      }
      const res = await api.get(`/site-inspection-points/definitions?${params.toString()}`, reqOpts);
      return siDataListFromResponse(res);
    },
    enabled: !!ssCode && !!situationKey,
    retry: false,
  });

  const {
    data: results,
    isLoading: isLoadingResults,
    refetch: refetchResults,
  } = useQuery({
    queryKey: ['site-inspection-points', 'results', buildingId, jobId, manualRefreshKey],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '500');
      params.set('buildingId', buildingId);
      if (jobId) params.set('jobId', jobId);
      const res = await api.get(`/site-inspection-points/results?${params.toString()}`, {
        validateStatus: siAxiosValidateEmptyOk,
      });
      return siDataListFromResponse(res);
    },
    enabled: !!buildingId,
    retry: false,
    refetchInterval: siApiLive && buildingId ? 8000 : false,
  });

  const {
    data: detectReportsRaw,
    isLoading: isLoadingDetect,
    refetch: refetchDetect,
  } = useQuery({
    queryKey: [
      'site-inspection-points',
      'detect-reports',
      buildingId,
      jobId,
      ssCode,
      situationKey,
      manualRefreshKey,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('limit', '200');
      params.set('buildingId', buildingId);
      if (jobId) params.set('jobId', jobId);
      if (ssCode) params.set('ssCode', ssCode);
      if (situationKey) params.set('situationKey', situationKey);
      const res = await api.get(`/site-inspection-points/detect-reports?${params.toString()}`, {
        validateStatus: siAxiosValidateEmptyOk,
      });
      return siDataListFromResponse(res);
    },
    enabled: !!buildingId,
    retry: false,
    refetchInterval: siApiLive && buildingId ? 8000 : false,
  });

  const detectReportsFiltered = useMemo(() => {
    const rows = Array.isArray(detectReportsRaw) ? detectReportsRaw : [];
    return rows.filter((r) => {
      if (!r) return false;
      if (ssCode && r.ss_code !== ssCode) return false;
      if (situationKey && r.situation_key !== situationKey) return false;
      return true;
    });
  }, [detectReportsRaw, ssCode, situationKey]);

  const latestByPoint = useMemo(() => {
    const map = {};
    const rows = Array.isArray(results) ? results : [];
    if (!rows.length) return map;

    for (const r of rows) {
      if (!r) continue;
      if (ssCode && r.ss_code !== ssCode) continue;
      if (situationKey && r.situation_key !== situationKey) continue;
      if (jobId && String(r.job_id) !== String(jobId)) continue;

      let pid = null;
      if (r.point_definition_id != null) pid = String(r.point_definition_id);
      else if (r.worker_added_line_id != null) pid = `w${r.worker_added_line_id}`;
      if (!pid) continue;
      if (!map[pid]) map[pid] = r;
    }
    return map;
  }, [results, ssCode, situationKey, jobId]);

  const defs = Array.isArray(definitions) ? definitions : [];
  const sortedDefs = [...defs].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
  const showPrint = defs.length > 0;

  const doRefresh = async () => {
    try {
      if (ssCode && situationKey) await refetchDefs();
      if (buildingId) {
        await refetchResults();
        await refetchDetect();
      }
    } finally {
      setManualRefreshKey((k) => k + 1);
    }
  };

  const printAnchorId = isInline ? 'site-inspection-print-inline' : 'site-inspection-print';

  const toolbarLabels = mergeCard ? 'text-[11px] text-gray-600' : 'text-sm text-gray-700';
  const inputSm = mergeCard ? 'px-1.5 py-1 text-xs rounded border-gray-300' : 'px-3 py-2 rounded-lg border-gray-300';

  const toolbarBlock = (
    <div className={`no-print flex flex-wrap items-end gap-2 sm:gap-3 ${mergeCard ? 'p-2 bg-slate-50 border-b border-gray-200' : ''}`}>
      <label className={toolbarLabels}>
        Inspection
        <input
          className={`border block mt-0.5 w-20 sm:w-24 ${inputSm}`}
          value={jobId}
          onChange={(e) => setJobId(e.target.value.replace(/\D/g, ''))}
          placeholder="opt"
        />
      </label>
      <label className={toolbarLabels}>
        SS
        <select
          className={`border block mt-0.5 w-36 sm:w-44 ${inputSm}`}
          value={ssCode}
          onChange={(e) => setSsCode(e.target.value)}
        >
          <option value="">—</option>
          {ssCodeRows.map(({ code, label }) => (
            <option key={code} value={code}>
              {label}
            </option>
          ))}
        </select>
      </label>
      <label className={toolbarLabels}>
        Situation
        <select
          className={`border block mt-0.5 w-36 sm:w-40 ${inputSm}`}
          value={situationKey}
          onChange={(e) => setSituationKey(e.target.value)}
        >
          {situationOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
      <label className={toolbarLabels}>
        <span className="flex items-center gap-1 mb-0.5">
          <User className={mergeCard ? 'w-3 h-3' : 'w-4 h-4'} />
          Worker need tag
        </span>
        <input
          className={`border block mt-0.5 w-28 sm:w-32 ${inputSm}`}
          value={workerRequirementTag}
          onChange={(e) => setWorkerRequirementTag(e.target.value)}
          placeholder="opt"
          title="worker_requirement_tag"
        />
      </label>
      <label className={toolbarLabels}>
        Term tag
        <input
          className={`border block mt-0.5 w-28 sm:w-32 ${inputSm}`}
          value={inspectionTermTag}
          onChange={(e) => setInspectionTermTag(e.target.value)}
          placeholder="opt"
          title="inspection_term_tag"
        />
      </label>
      <label className={toolbarLabels}>
        Location tag
        <input
          className={`border block mt-0.5 w-28 sm:w-32 ${inputSm}`}
          value={inspectionLocationTag}
          onChange={(e) => setInspectionLocationTag(e.target.value)}
          placeholder="opt"
          title="inspection_location_tag"
        />
      </label>
      <div className="flex items-center gap-1.5 pb-0.5">
        <button
          type="button"
          onClick={doRefresh}
          className={`inline-flex items-center gap-1 border border-gray-200 bg-white text-gray-700 rounded hover:bg-gray-50 ${mergeCard ? 'px-2 py-1 text-xs' : 'px-3 sm:px-4 py-2 text-sm'}`}
        >
          <RefreshCw className={mergeCard ? 'w-3 h-3' : 'w-4 h-4'} />
          Refresh
        </button>
        <button
          type="button"
          disabled={!showPrint}
          onClick={() => window.print()}
          className={`inline-flex items-center gap-1 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50 ${mergeCard ? 'px-2 py-1 text-xs' : 'px-3 sm:px-4 py-2 text-sm'}`}
        >
          <Printer className={mergeCard ? 'w-3 h-3' : 'w-4 h-4'} />
          Print
        </button>
      </div>
    </div>
  );

  const contextRow = (
    <div className={`bg-gray-50 border-b border-gray-200 ${mergeCard ? 'px-2 py-1' : 'p-3 sm:p-4'}`}>
      <div id={printAnchorId} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-gray-700">
        <span className={`inline-flex items-center gap-1 ${mergeCard ? 'text-[11px]' : 'text-sm'}`}>
          <MapPin className={mergeCard ? 'w-3 h-3' : 'w-4 h-4'} />
          {resolvedBuildingLabel ? `Building · ${resolvedBuildingLabel}` : buildingId ? 'Building · …' : 'Building · —'}
        </span>
        <span className={`inline-flex items-center gap-1 ${mergeCard ? 'text-[11px]' : 'text-sm'}`}>
          <Camera className={mergeCard ? 'w-3 h-3' : 'w-4 h-4'} />
          {ssCode || '—'} · {situationKey || '—'}
        </span>
        <span className={`text-gray-500 ${mergeCard ? 'text-[11px]' : 'text-sm'}`}>
          {jobId ? `Inspection #${jobId}` : 'All inspections'}
        </span>
        {(workerRequirementTag || inspectionTermTag || inspectionLocationTag) && (
          <span className={`text-gray-600 ${mergeCard ? 'text-[10px]' : 'text-xs'}`}>
            Scope: {[workerRequirementTag, inspectionTermTag, inspectionLocationTag].filter(Boolean).join(' · ')}
          </span>
        )}
      </div>
    </div>
  );

  const statusLine = (
    <div className={`text-gray-500 no-print ${mergeCard ? 'px-2 py-1 text-[10px] border-b border-gray-100 bg-white' : 'mt-3 text-xs'}`}>
      {isLoadingDefs && <span>Loading definitions… </span>}
      {isLoadingResults && <span>Loading results… </span>}
      {isLoadingDetect && <span>Loading detection reports…</span>}
      {!isLoadingDefs && defs.length === 0 && ssCode && <span>No rows loaded yet.</span>}
    </div>
  );

  const tableSection = (
    <div className={mergeCard ? 'p-1 sm:p-2' : isInline ? 'p-3 sm:p-4' : 'p-5'}>
          {defs.length === 0 ? (
            <div className="text-gray-500 text-sm space-y-2">
              {!ssCode && (
                <p>Select a building, SS code, and situation to load the checklist.</p>
              )}
              {!!ssCode && (isLoadingDefs || isLoadingResults) && <p>Loading checklist…</p>}
              {!!ssCode && !isLoadingDefs && !isLoadingResults && (
                <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span>No checklist rows for this SS and situation.</span>
                  <InfoButton title="Empty checklist ?? what to check" label="Info">
                    <p>
                      If the browser console shows 404 on{' '}
                      <code className="rounded bg-gray-100 px-1 text-xs">/api/site-inspection-points</code>, restart the
                      API from <code className="rounded bg-gray-100 px-1 text-xs">web/backend</code> (
                      <code className="rounded bg-gray-100 px-1 text-xs">npm run dev</code>).
                    </p>
                    <p>
                      If the API is OK but rows stay empty, run DB migrations and{' '}
                      <code className="rounded bg-gray-100 px-1 text-xs">npm run seed:site-inspection-sheets</code> in{' '}
                      <code className="rounded bg-gray-100 px-1 text-xs">web/backend</code>.
                    </p>
                  </InfoButton>
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1 print:overflow-visible">
              <table className="min-w-[1000px] w-full text-xs border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-100 text-gray-800">
                    <th className="border border-gray-300 px-1.5 py-2 text-left w-8">#</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left">Category</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[8rem]">Inspection item</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[7rem]">Info</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[5rem]">Method / tool</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[6rem]">Standard</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[4rem]">Location</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[3.5rem]">Value</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-center w-12">Unit</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-center w-16">
                      <span className="block">P/F</span>
                      <span className="block font-normal text-[10px] text-gray-500 normal-case">FAIL→DR</span>
                    </th>
                    <th className="border border-gray-300 px-1.5 py-2 text-center w-14">DR#</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[5rem]">Notes</th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[6rem]">
                      <span className="block">Photo</span>
                      <span className="block font-normal text-[10px] text-gray-500 normal-case">Worker field</span>
                    </th>
                    <th className="border border-gray-300 px-1.5 py-2 text-left min-w-[5rem]">Worker</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDefs.map((d, idx) => {
                    const pid = String(d.id);
                    const isWorkerAdded = d.is_worker_added === true || pid.startsWith('w');
                    const row = latestByPoint[pid];
                    const photos = parsePhotoUrls(row?.photo_urls);
                    const std = d.standard_ref || d.instruction || '';
                    const worker = row ? row.worker_name || row.worker_id : null;
                    const when = row?.created_at ? new Date(row.created_at).toLocaleString() : null;
                    return (
                      <tr key={pid} className={row ? 'bg-green-50/50' : ''}>
                        <td className="border border-gray-300 px-1.5 py-1.5 text-center align-top">{idx + 1}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top text-gray-700">
                          {d.category || '—'}
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top font-medium text-gray-900">
                          {d.title || `Row ${pid}`}
                          {isWorkerAdded ? (
                            <span className="ml-1 text-[10px] font-semibold text-amber-800">(added)</span>
                          ) : null}
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top max-w-[10rem]">
                          <details className="text-[11px]">
                            <summary className="cursor-pointer font-semibold text-indigo-800 select-none">Info</summary>
                            <pre className="mt-1 whitespace-pre-wrap text-[10px] text-gray-600 max-h-36 overflow-y-auto border border-gray-100 p-1.5 rounded bg-indigo-50/50">
                              {formatSiteInspectionPointInfo(d)}
                            </pre>
                          </details>
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top text-gray-600">{d.method_tool || '—'}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top text-gray-600">{std || '—'}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top">{row?.location_text || '—'}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top">{row?.measured_value || '—'}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 text-center align-top">{d.value_unit || '—'}</td>
                        <td className="border border-gray-300 px-1.5 py-1.5 text-center align-top font-semibold">
                          {row?.pass_fail ? (
                            <span
                              className={
                                row.pass_fail === 'FAIL'
                                  ? 'text-red-700'
                                  : row.pass_fail === 'PASS'
                                    ? 'text-green-700'
                                    : 'text-gray-600'
                              }
                            >
                              {row.pass_fail}
                            </span>
                          ) : (
                            <span className="text-gray-400 font-normal">—</span>
                          )}
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 text-center align-top text-[10px]">
                          {row?.pass_fail === 'FAIL' && row?.detect_report_id != null ? (
                            <span className="font-mono text-rose-800 font-semibold">DR#{row.detect_report_id}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top whitespace-pre-wrap max-w-[10rem]">
                          {row?.note_text || '—'}
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top">
                          <div className="flex flex-wrap gap-1">
                            {photos.map((u, i) => (
                              <a key={i} href={photoSrc(u)} target="_blank" rel="noreferrer" className="block">
                                <img
                                  src={photoSrc(u)}
                                  alt=""
                                  className="h-14 w-14 rounded border border-gray-200 object-cover"
                                />
                              </a>
                            ))}
                          </div>
                        </td>
                        <td className="border border-gray-300 px-1.5 py-1.5 align-top text-gray-500">
                          {row ? (
                            <span className="inline-flex items-start gap-1">
                              <User className="w-3 h-3 shrink-0 mt-0.5" />
                              <span>
                                {worker || '—'}
                                {when ? <span className="block text-[10px]">{when}</span> : null}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">Pending</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
    </div>
  );

  const detectReportsSection = (
    <div
      className={`no-print border-t border-rose-100 bg-rose-50/40 ${mergeCard ? 'p-2' : 'p-4'}`}
    >
      <h3
        className={`font-semibold text-rose-950 flex flex-wrap items-center gap-x-2 gap-y-1 ${mergeCard ? 'text-xs' : 'text-sm'}`}
      >
        <span className="inline-flex items-center gap-2">
          <FileText className={mergeCard ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          Detection reports (FAIL)
        </span>
        <InfoButton title="Detection reports (FAIL)" label="Info">
          <p>
            When a line is marked FAIL, a detection report can be generated and saved automatically for audit. Listed
            here for this building (and filtered by SS / situation when set).
          </p>
        </InfoButton>
      </h3>
      {!buildingId ? (
        <p className="text-xs text-gray-500 mt-1">Select a building to list reports.</p>
      ) : detectReportsFiltered.length === 0 && !isLoadingDetect ? (
        <p className="text-xs text-gray-500 mt-1">None for this filter.</p>
      ) : (
        <div className="mt-2 space-y-2 max-h-[28rem] overflow-y-auto">
          {detectReportsFiltered.map((dr) => (
            <details
              key={dr.id}
              className="border border-gray-200 rounded-md bg-white text-xs shadow-sm"
            >
              <summary className="cursor-pointer px-2 py-1.5 font-medium text-gray-800">
                DR#{dr.id} · {dr.report_title || '—'}
              </summary>
              <pre className="border-t border-gray-100 px-2 py-2 text-[10px] text-gray-700 whitespace-pre-wrap max-h-48 overflow-y-auto">
                {dr.report_body || '—'}
              </pre>
              {dr.structured && typeof dr.structured === 'object' ? (
                <details className="border-t border-gray-100 px-2 py-1">
                  <summary className="cursor-pointer text-[10px] text-gray-600">Structured JSON</summary>
                  <pre className="mt-1 text-[9px] text-gray-500 whitespace-pre-wrap max-h-32 overflow-y-auto">
                    {JSON.stringify(dr.structured, null, 2)}
                  </pre>
                </details>
              ) : null}
            </details>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={mergeCard ? 'space-y-2' : isInline ? 'space-y-3' : 'space-y-6'}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
        }
      `}</style>

      {pingFetched && pingState && !pingState.ok && (
        <div className="no-print rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-950 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">Site inspection API unavailable.</span>
          <InfoButton title="Site inspection API troubleshooting" label="Info">
            <p>
              This screen calls the Node routes under{' '}
              <code className="rounded bg-gray-100 px-1">/api/site-inspection-points</code> (same process as the rest of
              the BWOF API, default port <code className="rounded bg-gray-100 px-1">3000</code>).
            </p>
            {pingState.networkError && (
              <p>
                Could not reach the API ?? run <code className="rounded bg-gray-100 px-1">cd web/backend && npm run dev</code>
                (MySQL must be running first). In dev, leave <code className="rounded bg-gray-100 px-1">VITE_API_BASE_URL</code> unset
                so the browser calls <code className="rounded bg-gray-100 px-1">/api</code> on the Vite host and the proxy forwards to
                port 3000.
              </p>
            )}
            {!pingState.networkError && pingState.status === 404 && (
              <p>
                HTTP 404 ?? the URL the app is calling is not serving this route (wrong port, old server, or different
                project). Start this repo&apos;s API from{' '}
                <code className="rounded bg-gray-100 px-1">web/backend</code> with{' '}
                <code className="rounded bg-gray-100 px-1">npm run dev</code>.
              </p>
            )}
            {!pingState.networkError && pingState.status > 0 && pingState.status !== 404 && (
              <p>HTTP {pingState.status} — check API logs.</p>
            )}
            <p>
              Quick check (same URL the app uses for ping): open{' '}
              <code className="rounded bg-gray-100 px-1">/api/site-inspection-points/ping</code> on this page&apos;s host in
              this browser ?? full URL:{' '}
              <code className="rounded bg-gray-100 px-1 break-all">{getSiteInspectionPingUrl()}</code>. Expect{' '}
              <code className="rounded bg-gray-100 px-1">ok: true</code> in JSON (this API may also include a{' '}
              <code className="rounded bg-gray-100 px-1">route</code> field).
            </p>
          </InfoButton>
        </div>
      )}

      {!isInline && (
        <PageHeading
          title="Site inspection"
          infoTitle="Site inspection"
          infoChildren={
            <p>
              Same rows as the worker app. Use per-row <strong>Info</strong> for consolidated guidance; photos are
              captured in the field by workers. Print when you need paper evidence.
            </p>
          }
        />
      )}

      {mergeCard ? (
        <div className="rounded-lg border border-gray-200 overflow-hidden bg-white shadow-sm">
          {toolbarBlock}
          {statusLine}
          {contextRow}
          {tableSection}
          {detectReportsSection}
        </div>
      ) : (
        <>
          <div className={`bg-white border border-gray-200 rounded-lg no-print ${isInline ? 'p-3' : 'p-5'}`}>
            <div className="flex flex-wrap items-end gap-3 sm:gap-4">
              {defaultApplicationId ? (
                <div className="text-sm text-gray-700 min-w-[12rem]">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4" />
                    Building
                  </div>
                  <div className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-800 max-w-md">
                    {resolvedBuildingLabel || (buildingId ? 'Loading…' : '—')}
                  </div>
                </div>
              ) : (
                <label className="text-sm text-gray-700 min-w-[14rem] flex-1 max-w-md">
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4" />
                    Building
                  </div>
                  <select
                    className="border border-gray-300 rounded-lg px-3 py-2 w-full block mt-1"
                    value={buildingId}
                    onChange={(e) => setBuildingId(e.target.value)}
                  >
                    <option value="">Select building</option>
                    {buildingsArray.map((b) => {
                      const n = (b.name || '').trim() || 'Building';
                      const a = (b.address || '').trim();
                      const sub = a.length > 72 ? `${a.slice(0, 72)}…` : a;
                      return (
                        <option key={b.id} value={String(b.id)}>
                          {a ? `${n} — ${sub}` : n}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              <label className="text-sm text-gray-700">
                Inspection ID (optional)
                <input
                  className="border border-gray-300 rounded-lg px-3 py-2 w-24 sm:w-28 block mt-1"
                  value={jobId}
                  onChange={(e) => setJobId(e.target.value.replace(/\D/g, ''))}
                  placeholder=""
                />
              </label>
              <label className="text-sm text-gray-700">
                SS code
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 w-44 sm:w-48 block mt-1"
                  value={ssCode}
                  onChange={(e) => setSsCode(e.target.value)}
                >
                  <option value="">Select SS</option>
                  {ssCodeRows.map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-sm text-gray-700">
                Situation
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2 w-44 sm:w-48 block mt-1"
                  value={situationKey}
                  onChange={(e) => setSituationKey(e.target.value)}
                >
                  {situationOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={doRefresh}
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button>
                <button
                  type="button"
                  disabled={!showPrint}
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>
            <div className="mt-3 text-xs text-gray-500">
              {isLoadingDefs && <span>Loading checklist definitions… </span>}
              {isLoadingResults && <span>Loading worker results…</span>}
              {!isLoadingDefs && defs.length === 0 && ssCode && <span>No checkpoints for this SS + situation.</span>}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            {contextRow}
            {tableSection}
            {detectReportsSection}
          </div>
        </>
      )}
    </div>
  );
}
